/**
 * One month of one room in the occupancy matrix (Slice 4, corrected in S4.1).
 *
 * SEVEN BUSINESS STATES, THREE FAMILIES, AND ONE TECHNICAL STATE THAT IS NOT A
 * BUSINESS STATE AT ALL. Family is how the cell is coloured; state is what it
 * means. `secure` and `notice_given` are both occupied and both green, but a
 * manager reading the row needs to see that one of them has notice on it — so
 * the state keeps its own visual variant and its own word, and the word is
 * always written out.
 *
 * THE TWO FREE STATES ARE NOT THE SAME ANSWER. `free_planned` is free BECAUSE a
 * known contract end says so; `free_unknown` is free with nothing to explain
 * it. `docs/KPI_SEMANTICS.md` §E.4 names both, and before S4.1 this page had
 * one word, "Frei", doing both jobs. They share the rose family and stay
 * separable by border style, exactly as the old pair did.
 *
 * `unknown` IS NEUTRAL ON PURPOSE. It says the calendar data for this month
 * could not be read, not that the room is free. Giving it a rose cell would be
 * the same mistake in a new coat: the reader would come away with a vacancy
 * they can act on, from a month nobody answered for. Muted tokens, its own
 * family, its own word.
 *
 * NOTHING IS CONVEYED BY COLOUR ALONE. Each cell prints its German label and
 * carries an `aria-label` naming the room, the month and the state.
 *
 * `notice_given` DELIBERATELY KEEPS THE OCCUPIED GREEN and adds an amber edge
 * rather than turning amber outright: amber is what `reserved` means here, and a
 * fully amber notice-given month would read as a room somebody is moving into
 * rather than one somebody is moving out of.
 */
import React from "react";

import CalendarTransitionPopover from "./CalendarTransitionPopover";
import { UNKNOWN_STATE } from "./occupancyCalendarModel";
import { describeTransitions } from "../../../utils/lifecycleCalendarTruth";

/**
 * Per-state classes. Family sets the hue, the border style separates states
 * inside a family. Tailwind palette steps with a dark counterpart each, plus
 * the theme's own neutral tokens for `unknown` — no hex.
 *
 * EXPORTED FOR THE MOBILE VIEW (UI5), which paints the same eight states in a
 * different layout. A second copy of this map would be a third vocabulary for
 * one payload — the legend's swatches already keep their own reduced set — and
 * the two would drift the first time a state's tint is adjusted.
 */
export const STATE_CLASSES = {
  secure:
    "border-solid bg-emerald-500/15 border-emerald-500/40 text-emerald-800 dark:text-emerald-200",
  cancellable:
    "border-dashed bg-emerald-500/10 border-emerald-500/50 text-emerald-800 dark:text-emerald-200",
  always_cancellable:
    "border-dotted bg-emerald-500/[0.07] border-emerald-500/60 text-emerald-800 dark:text-emerald-200",
  notice_given:
    "border-solid bg-emerald-500/15 border-emerald-500/40 text-emerald-800 dark:text-emerald-200",
  reserved:
    "border-solid bg-amber-500/20 border-amber-500/45 text-amber-900 dark:text-amber-200",
  free_planned:
    "border-dashed bg-rose-500/10 border-rose-500/45 text-rose-800 dark:text-rose-200",
  free_unknown:
    "border-solid bg-rose-500/15 border-rose-500/35 text-rose-800 dark:text-rose-200",
  [UNKNOWN_STATE]: "border-dashed bg-muted border-border text-muted-foreground",
};

export default function CoLivingOccupancyCell({ cell, roomName }) {
  // Fails CLOSED: a state with no variant is a data problem, so it borrows the
  // neutral one rather than the free one it used to borrow.
  const stateClass = STATE_CLASSES[cell.state] || STATE_CLASSES[UNKNOWN_STATE];

  // R8: what changes INSIDE this month. The cell keeps its own state, its own
  // colour and its own word — this is added beside them, never instead of them.
  const transitions = cell.transitions || [];
  const transitionText = describeTransitions(transitions);
  const base = `${roomName}, ${cell.monthLabel}: ${cell.label}`;

  return (
    // The current month is tinted for the whole column height, not just in its
    // header (UI4). Nine columns of identical 60px boxes give the eye nothing
    // to hold on to, and "which of these is now?" is the first question asked
    // of a calendar. A tint on the `td` rather than on the cell keeps every
    // state's own surface exactly as it was.
    <td className={`p-[3px] align-middle ${cell.isCurrent ? "bg-primary/[0.04]" : ""}`}>
      <div
        data-lifecycle-state={cell.state}
        data-lifecycle-family={cell.family}
        data-lifecycle-transitions={transitions.length || undefined}
        aria-label={transitionText ? `${base}. ${transitionText}` : base}
        title={`${roomName} · ${cell.monthLabel} · ${cell.label}${
          transitionText ? ` · ${transitionText}` : ""
        }`}
        className={`flex h-[34px] items-center justify-center rounded-[6px] border px-1 text-center text-[10px] font-semibold leading-tight ${stateClass}`}
      >
        {cell.label}
        {cell.state === "notice_given" ? (
          // The notice marker: occupied, but with an end already announced.
          <span
            className="ml-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
            aria-hidden="true"
          />
        ) : null}
        {/*
          THE ARROW IS NOW THE BUTTON IT WAS PRETENDING TO BE (UI4). R8 drew it
          `aria-hidden` and put the wording in this cell's `aria-label` and
          `title` — reachable by a screen reader and by a mouse, and by nobody
          on a keyboard or a phone. The glyph keeps its place, its size and its
          meaning and gains a popover; the `aria-label` above is untouched, so
          the assistive-technology route R8 shipped is still there underneath.
          Nothing renders here when the month is quiet.
        */}
        <CalendarTransitionPopover
          transitions={transitions}
          roomName={roomName}
          monthLabel={cell.monthLabel}
        />
      </div>
    </td>
  );
}
